import { useEffect, useMemo, useState } from "react";
import { X, Star, Package, Search, ChevronLeft, Sparkles, Check, Loader2 } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD } from "../lib/format";

const TIER_META = {
  good: { label: "Good", color: "bg-slate-100 text-slate-800", border: "border-slate-300", accent: "from-slate-50 to-white" },
  better: { label: "Better", color: "bg-sky-100 text-sky-800", border: "border-sky-300", accent: "from-sky-50 to-white" },
  best: { label: "Best", color: "bg-amber-100 text-amber-900", border: "border-amber-300", accent: "from-amber-50 to-white" },
};

/**
 * QuickQuoteModal — pick a Good/Better/Best service from the Price Book and convert it
 * into an Estimate for the given customer in two taps.
 *
 * Flow:
 *   Step 1: pick a variant group (a service with G/B/B tiers) OR a single item
 *   Step 2: see the tiers side-by-side, tap one, create estimate
 */
export default function QuickQuoteModal({ customerId, customerName, onClose, onCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [step, setStep] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/pricebook").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);

  // Group by variant_group (only multi-tier groups for step 1)
  const { variantGroups, soloItems } = useMemo(() => {
    const groups = new Map();
    const solos = [];
    for (const it of items) {
      const q = search.toLowerCase();
      if (q && !(it.name + " " + (it.description || "")).toLowerCase().includes(q)) continue;
      if (it.variant_group) {
        if (!groups.has(it.variant_group)) groups.set(it.variant_group, []);
        groups.get(it.variant_group).push(it);
      } else {
        solos.push(it);
      }
    }
    const order = { good: 0, better: 1, best: 2, "": 3 };
    const vgs = [...groups.entries()]
      .filter(([_, arr]) => arr.length > 1)
      .map(([k, arr]) => ({
        key: k,
        baseName: arr.find((i) => i.variant_tier === "good")?.name?.split(" — ")[0] || arr[0].name.split(" — ")[0],
        items: arr.sort((a, b) => order[a.variant_tier || ""] - order[b.variant_tier || ""]),
      }));
    return { variantGroups: vgs, soloItems: solos };
  }, [items, search]);

  const create = async (item) => {
    setCreating(true);
    try {
      const payload = {
        customer_id: customerId,
        status: "Sent",  // Sent so the customer can see it in portal
        line_items: [{
          name: item.name,
          description: item.description || "",
          quantity: 1,
          rate: Number(item.sale_price || 0),
          unit: item.unit || "ea",
          taxable: item.taxable !== false,
          pricebook_item_id: item.id,
        }],
        invoice_discount_type: "amount",
        invoice_discount_value: 0,
        notes: `Quick-quote · ${item.variant_tier ? `${item.variant_tier.charAt(0).toUpperCase()}${item.variant_tier.slice(1)} tier` : "Standalone option"}`,
      };
      const { data: estimate } = await api.post("/estimates", payload);
      onCreated?.(estimate);
    } catch (e) {
      alert(formatErr(e));
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="quick-quote-modal" className="bg-white rounded-lg w-full max-w-4xl shadow-xl max-h-[90vh] flex flex-col overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button onClick={() => { setStep(1); setSelectedGroup(null); }} className="p-1 text-slate-400 hover:text-slate-900 rounded"><ChevronLeft size={16}/></button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-500"/>
                <h2 className="font-display text-xl font-bold text-slate-900">Quick Quote</h2>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">For {customerName} · {step === 1 ? "Pick a service" : `${selectedGroup?.baseName} — pick a tier`}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900 rounded"><X size={18}/></button>
        </header>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-slate-400"/></div>
        ) : step === 1 ? (
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input
                  autoFocus
                  data-testid="qq-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search services & materials"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
                />
              </div>
            </div>

            <div className="px-6 py-5 space-y-6">
              {variantGroups.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
                    <Star size={12} className="text-amber-500"/> Tiered packages ({variantGroups.length})
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {variantGroups.map((g) => (
                      <button
                        key={g.key}
                        onClick={() => { setSelectedGroup(g); setStep(2); }}
                        data-testid={`qq-group-${g.key}`}
                        className="text-left border border-slate-200 rounded-md p-4 hover:border-[#0F4C81] hover:shadow-sm transition-all"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <div className="font-semibold text-slate-900">{g.baseName}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{g.items[0].category}{g.items[0].subcategory ? ` › ${g.items[0].subcategory}` : ""}</div>
                          </div>
                          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">{g.items.length}-tier</span>
                        </div>
                        <div className="flex gap-1.5 mt-3">
                          {g.items.map((i) => (
                            <span key={i.id} className={`text-[10px] px-1.5 py-0.5 rounded ${TIER_META[i.variant_tier]?.color || "bg-slate-100 text-slate-700"}`}>
                              {TIER_META[i.variant_tier]?.label || "Std"} · {fmtCAD(i.sale_price)}
                            </span>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {soloItems.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Standalone items ({soloItems.length})</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {soloItems.map((it) => (
                      <button
                        key={it.id}
                        onClick={() => create(it)}
                        disabled={creating}
                        data-testid={`qq-solo-${it.id}`}
                        className="text-left border border-slate-200 rounded-md p-3 hover:border-[#0F4C81] hover:shadow-sm transition-all flex items-center gap-3 disabled:opacity-50"
                      >
                        {it.image_path
                          ? <img src={fileUrl(`/pricebook/${it.id}/image`)} alt="" className="w-10 h-10 rounded object-cover border border-slate-200 shrink-0"/>
                          : <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-slate-300 shrink-0"><Package size={16}/></div>}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-slate-900 truncate">{it.name}</div>
                          <div className="text-xs text-slate-500">{fmtCAD(it.sale_price)} / {it.unit}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {variantGroups.length === 0 && soloItems.length === 0 && (
                <div className="text-center py-12 text-slate-500 text-sm">No price book items match your search.</div>
              )}
            </div>
          </div>
        ) : (
          // Step 2 — tier picker
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="grid sm:grid-cols-3 gap-4" data-testid="qq-tier-grid">
              {selectedGroup?.items.map((it) => {
                const meta = TIER_META[it.variant_tier] || TIER_META.good;
                return (
                  <button
                    key={it.id}
                    onClick={() => create(it)}
                    disabled={creating}
                    data-testid={`qq-tier-${it.variant_tier}`}
                    className={`text-left border-2 ${meta.border} rounded-lg p-4 bg-gradient-to-b ${meta.accent} hover:shadow-md transition-all disabled:opacity-50 group relative overflow-hidden`}
                  >
                    <div className={`absolute top-3 right-3 text-[10px] uppercase tracking-wider font-bold ${meta.color} px-2 py-0.5 rounded`}>{meta.label}</div>
                    {it.image_path && (
                      <img src={fileUrl(`/pricebook/${it.id}/image`)} alt="" className="w-full aspect-video object-cover rounded mb-3"/>
                    )}
                    <div className="font-display text-lg font-bold text-slate-900 pr-12">{it.name.replace(/^.* — /, "")}</div>
                    {it.description && <p className="text-xs text-slate-600 mt-1 line-clamp-3">{it.description}</p>}
                    <div className="mt-4 pt-4 border-t border-slate-200/70 flex items-end justify-between">
                      <div>
                        <div className="font-display text-3xl font-bold tabular text-slate-900">{fmtCAD(it.sale_price)}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">per {it.unit}</div>
                      </div>
                      <span className="text-xs text-[#0F4C81] font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Check size={12}/> Select
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-5 text-xs text-slate-500 text-center">Click a tier to create a sent estimate for {customerName} — your customer can approve it from the portal.</p>
          </div>
        )}

        {creating && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <div className="bg-white border border-slate-200 rounded-lg px-5 py-3 shadow-lg flex items-center gap-3">
              <Loader2 size={16} className="animate-spin text-[#0F4C81]"/>
              <span className="text-sm text-slate-700">Creating estimate…</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
